/**
 * NotesApiClient -- HTTP client for /api/v1/notes endpoints
 * (PANEL-NOTES-V1-001 T2 backend / T3 UI).
 *
 * 7 metodos correspondientes a los 7 endpoints REST entregados en T2:
 *   list, getById, create, update, remove (soft), uploadAttachment, getAttachmentUrl
 *
 * @module infrastructure/api/NotesApiClient
 */

import { AppConfig } from '../../config.js';
import { ApiBase } from './ApiBase.js';
import { ok, fail } from '../../domain/value-objects/CrudResult.js';

const BASE = '/api/v1/notes';

export const NotesApiClient = {
  /**
   * Lista paginada con filtros.
   * @param {object} params - { userUid?, businessId?, category?, status?, priority?, tag?, page?, pageSize? }
   */
  async list(params = {}) {
    const qs = new URLSearchParams();
    if (params.userUid)    qs.set('userUid',    params.userUid);
    if (params.businessId) qs.set('businessId', params.businessId);
    if (params.category)   qs.set('category',   params.category);
    if (params.status)     qs.set('status',     params.status);
    if (params.priority)   qs.set('priority',   params.priority);
    if (params.tag)        qs.set('tag',        params.tag);
    if (params.page)       qs.set('page',       String(params.page));
    if (params.pageSize)   qs.set('pageSize',   String(params.pageSize));
    const query = qs.toString();
    return ApiBase.get(`${BASE}${query ? `?${query}` : ''}`);
  },

  /** Detalle + attachments hidratados. */
  async getById(noteId) {
    return ApiBase.get(`${BASE}/${noteId}`);
  },

  /** Crear nota: { title, body?, category, priority, businessId?, tags?, metadata? } */
  async create(payload) {
    return ApiBase.post(BASE, payload);
  },

  /** Editar parcial: cualquier subset de los campos del create + status. */
  async update(noteId, payload) {
    return ApiBase.patch(`${BASE}/${noteId}`, payload);
  },

  /** Soft delete (status=discarded). */
  async remove(noteId) {
    return ApiBase.del(`${BASE}/${noteId}`);
  },

  /**
   * Subir attachment via multipart/form-data.
   * No usa ApiBase porque ApiBase fuerza Content-Type: application/json.
   * Sigue patron de AuditoriaApiClient.extract.
   * @param {string} noteId
   * @param {File} file
   * @returns {Promise<{ok:boolean, data?:object, error?:object}>}
   */
  async uploadAttachment(noteId, file) {
    const url = `${AppConfig.apiBase}${BASE}/${noteId}/attachments`;
    const formData = new FormData();
    formData.append('file', file);

    const headers = {};
    if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;

    try {
      const res = await ApiBase.fetchQueCambia(url, { method: 'POST', body: formData, headers });
      const data = await res.json().catch(() => null);
      if (res.status === 401) {
        AppConfig.logout?.();
        return fail('UNAUTHORIZED', 'Sesion expirada');
      }
      if (!res.ok) {
        const msg = data?.detail || data?.message || `Error ${res.status}`;
        return fail(res.status, msg, data);
      }
      return ok(data);
    } catch (err) {
      return fail('NETWORK', err?.message || 'Error de red');
    }
  },

  /**
   * Construye la URL absoluta para un attachment binario.
   * Para imagenes pasa thumb=1 si quieres el thumbnail 200x200.
   * El JWT NO va en la URL — el panel usa <img src> con un proxy o pasa Authorization
   * via fetch + URL.createObjectURL para preview seguro.
   */
  attachmentUrl(noteId, fileId, { thumb = false } = {}) {
    const t = thumb ? '?thumb=1' : '';
    return `${AppConfig.apiBase}${BASE}/${noteId}/attachments/${fileId}${t}`;
  },

  /**
   * Descarga el binario via fetch con auth y devuelve un blob URL para usar en <img src>.
   * Llamar `URL.revokeObjectURL` cuando la imagen ya no se necesite.
   */
  async fetchAttachmentBlobUrl(noteId, fileId, { thumb = false, meta = null } = {}) {
    // #2495 NOTES T7: `meta` (opcional) captura el status HTTP para que el caller
    // distinga 410 GONE (attachment legacy anterior al fix de storage backend) de un
    // error transitorio. No-breaking: los callers que no pasan meta se comportan igual.
    const url = this.attachmentUrl(noteId, fileId, { thumb });
    const headers = {};
    if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;
    let res;
    try {
      res = await fetch(url, { method: 'GET', headers });
    } catch (err) {
      if (meta) { meta.status = 0; meta.error = err?.message || 'network'; }
      return null;
    }
    if (meta) meta.status = res.status;
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Descarga un attachment como archivo en el sistema del usuario (trigger save dialog).
   * Hace fetch con auth, crea un <a download> temporal y dispara el click.
   * @param {string} noteId
   * @param {string} fileId
   * @param {string} fileName - nombre sugerido al guardar
   * @returns {Promise<boolean>} true si la descarga inicio
   */
  async downloadAttachment(noteId, fileId, fileName = 'attachment') {
    const blobUrl = await this.fetchAttachmentBlobUrl(noteId, fileId, { thumb: false });
    if (!blobUrl) return false;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }, 200);
    return true;
  },

  /**
   * Abre un attachment en pestana nueva (preview en el navegador).
   * Util para PDFs y para imagenes cuando no hay lightbox.
   */
  async openAttachmentInNewTab(noteId, fileId) {
    const blobUrl = await this.fetchAttachmentBlobUrl(noteId, fileId, { thumb: false });
    if (!blobUrl) return false;
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    // No revocamos inmediato: el blob URL debe estar vivo para que la pestana cargue.
    // Cleanup pasivo tras 60s (tiempo suficiente para cargar).
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return true;
  }
};
